import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Inbox, Check, X as XIcon, EyeOff, Plus, Search, ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { BridgeApi, RequestsApi } from "../lib/api";
import { qk } from "../lib/query";
import { useStreamEvent } from "../lib/sse";
import { useToast } from "../lib/toast";
import { PageHeader } from "../components/PageScaffold";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { PlatformBadge } from "../components/badges";
import { relativeTime, localDateTime } from "../lib/format";
import { cn } from "../lib/cn";
import { RouteFields } from "./Routes";
import type { Route, RouteRequest, UnmappedMessage, Platform, ChatKind } from "../lib/types";

const PAGE_SIZE = 25;

export default function Requests() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showHidden, setShowHidden] = useState(false);
  const [status, setStatus] = useState<"unresolved" | "resolved" | "all">("unresolved");
  const [platform, setPlatform] = useState<string>("");
  const [channel, setChannel] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [debouncedQ, setDebouncedQ] = useState<string>("");
  const [page, setPage] = useState(1);
  const [selectedChat, setSelectedChat] = useState<string | null>(null);

  // Debounce keyword input so we're not refetching on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 1 when filters change.
  useEffect(() => { setPage(1); }, [showHidden, status, platform, channel, debouncedQ]);

  const listOpts = { showHidden, status, platform: platform || undefined, channel: channel || undefined, q: debouncedQ || undefined, page, pageSize: PAGE_SIZE };
  const { data, isLoading, error } = useQuery({
    queryKey: qk.requests.list(listOpts),
    queryFn: () => RequestsApi.list(listOpts),
    placeholderData: (prev) => prev,
  });
  const requests = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = data?.pageCount ?? 1;
  const pendingCount = useMemo(() => requests.filter((r) => !r.resolved).length, [requests]);

  const { data: cfg } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });
  const { data: channels = [] } = useQuery({
    queryKey: ["bridge", "channels"],
    queryFn: async () => {
      const res = await fetch("/api/bridge/channels");
      if (!res.ok) return [] as Array<{ id: number; name: string; platform: string }>;
      return res.json() as Promise<Array<{ id: number; name: string; platform: string }>>;
    },
  });

  useStreamEvent("request", () => {
    qc.invalidateQueries({ queryKey: ["requests"] });
  });

  const personaNames = cfg ? Object.keys(cfg.personas) : [];
  const mcpByPersona = cfg?.mcp_servers_by_persona ?? {};
  const allRoutes = cfg?.routes ?? [];
  // Channels configured for observer auto-route (persona + engagement=observe):
  // a request on such a channel can be one-click provisioned from the channel's rule.
  const provisioningByChannel = useMemo(() => {
    const m = new Map<string, { channelId: number; persona: string }>();
    for (const ch of cfg?.channels ?? []) {
      if (ch.default_persona && ch.default_engagement === "observe") m.set(ch.name, { channelId: ch.id, persona: ch.default_persona });
    }
    return m;
  }, [cfg]);
  const provFor = (r: RouteRequest) => (r.channel ? provisioningByChannel.get(r.channel) : undefined);
  const platformOptions = useMemo(
    () => [...new Set(channels.map((ch) => ch.platform))].sort(),
    [channels],
  );
  const channelOptions = useMemo(
    () => channels.filter((ch) => !platform || ch.platform === platform),
    [channels, platform],
  );

  // Per-chat actions (hide / dismiss / resolve) all remove the chat from the
  // current (default "unresolved") view. We update the cache OPTIMISTICALLY
  // instead of relying solely on an invalidate→refetch: consecutive actions
  // were leaving the just-actioned row on the list until a manual refresh
  // (a stale in-flight refetch could win the race and re-show it). cancelQueries
  // kills any in-flight fetch so it can't clobber us; we drop the chat from
  // every cached requests-list page; onSettled reconciles with the server.
  type ReqPage = { items: RouteRequest[]; total: number; page: number; pageSize: number; pageCount: number };
  const dropChatFromCache = async (chatJid: string) => {
    await qc.cancelQueries({ queryKey: ["requests"] });
    const snapshot = qc.getQueriesData({ queryKey: ["requests"] });
    qc.setQueriesData({ queryKey: ["requests"] }, (old: unknown) => {
      const page = old as ReqPage | undefined;
      if (!page?.items) return old;               // skip non-list caches (e.g. conversation arrays)
      const items = page.items.filter((r) => r.chat_jid !== chatJid);
      if (items.length === page.items.length) return old;
      return { ...page, items, total: Math.max(0, page.total - 1) };
    });
    setSelectedChat(null);
    return snapshot;
  };
  const restoreCache = (snapshot?: Array<[readonly unknown[], unknown]>) => {
    snapshot?.forEach(([key, data]) => qc.setQueryData(key, data));
  };
  const chatActionConfig = (mutationFn: (chat: string) => Promise<void>, successMsg?: string) => ({
    mutationFn,
    onMutate: (chatJid: string) => dropChatFromCache(chatJid),
    onError: (_e: unknown, _chatJid: string, snapshot: unknown) => { restoreCache(snapshot as never); toast.error("Action failed — restored"); },
    onSuccess: () => { if (successMsg) toast.success(successMsg); },
    onSettled: () => { qc.invalidateQueries({ queryKey: ["requests"] }); },
  });

  const hideMut = useMutation(chatActionConfig(RequestsApi.hideChat));
  const dismissMut = useMutation(chatActionConfig(RequestsApi.dismissChat, "Dismissed"));
  const resolveMut = useMutation(chatActionConfig(RequestsApi.resolveChat, "Resolved"));
  // "Wiki & resolve": enqueue the conversation (+ its links) for the wiki curator,
  // then resolve. Same optimistic removal as the others, with a count-aware toast.
  const wikiMut = useMutation({
    mutationFn: RequestsApi.wikiChat,
    onMutate: (chatJid: string) => dropChatFromCache(chatJid),
    onError: (_e, _chatJid, snapshot) => { restoreCache(snapshot as never); toast.error("Wiki submit failed — restored"); },
    onSuccess: (res) => {
      const links = res?.links ? ` + ${res.links} link${res.links === 1 ? "" : "s"}` : "";
      toast.success("Queued for wiki & resolved", `The curator will extract + tag it shortly${links}.`);
    },
    onSettled: () => { qc.invalidateQueries({ queryKey: ["requests"] }); },
  });

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Requests"
        icon={Inbox}
        subtitle={`${pendingCount} pending on page · ${total} total match`}
        actions={
          <label className="flex items-center gap-1.5 text-xs text-fg-muted cursor-pointer">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="size-3.5"
            />
            show hidden
          </label>
        }
      />

      {/* Filter bar */}
      <div className="px-5 pb-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search chat ID, sender, body…"
            className="w-full pl-8 text-sm"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as "unresolved" | "resolved" | "all")}
          className="text-sm"
          title="Filter by resolution status"
        >
          <option value="unresolved">Unresolved</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
        <select
          value={platform}
          onChange={(e) => { setPlatform(e.target.value); setChannel(""); }}
          className="text-sm"
        >
          <option value="">All platforms</option>
          {platformOptions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="text-sm"
        >
          <option value="">All channels</option>
          {channelOptions.map((ch) => (
            <option key={ch.id} value={ch.name}>{ch.name}</option>
          ))}
        </select>
        {(platform || channel || debouncedQ) && (
          <button
            onClick={() => { setPlatform(""); setChannel(""); setQ(""); }}
            className="text-xs text-fg-subtle hover:text-fg px-2 py-1"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 panel mx-5 mb-3 overflow-hidden flex">
        <ListDetail<RouteRequest>
          items={requests}
          itemKey={(r) => r.chat_jid}
          selectedId={selectedChat}
          onSelect={setSelectedChat}
          loading={isLoading}
          error={error as Error | null}
          itemHeight={76}
          emptyState={
            <EmptyState
              icon={<Inbox className="size-8" />}
              title="No requests"
              description={debouncedQ || platform || channel
                ? "No matches for the current filters."
                : "Unknown conversations will appear here for approval."}
            />
          }
          renderItem={({ item }) => <RequestRow req={item} provisioning={provFor(item)} />}
          renderDetail={(r) => (
            <RequestDetail
              req={r}
              personaNames={personaNames}
              mcpByPersona={mcpByPersona}
              allRoutes={allRoutes}
              channels={channels}
              provisioning={provFor(r)}
              onCreated={(name) => {
                qc.invalidateQueries({ queryKey: qk.bridge.config });
                qc.invalidateQueries({ queryKey: ["requests"] });
                setSelectedChat(null);
                toast.success(`Route "${name}" created`);
              }}
              onHide={() => hideMut.mutate(r.chat_jid)}
              onDismiss={() => dismissMut.mutate(r.chat_jid)}
              onResolve={() => resolveMut.mutate(r.chat_jid)}
              onWiki={() => wikiMut.mutate(r.chat_jid)}
            />
          )}
        />
      </div>

      {/* Pagination */}
      <div className="px-5 pb-5 flex items-center gap-2 text-xs text-fg-muted">
        <span className="tabular-nums">
          {total === 0 ? "0" : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)}`} of {total}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-1.5 rounded hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft className="size-3.5" />
          </button>
          <span className="tabular-nums">Page {page} / {pageCount}</span>
          <button
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={page >= pageCount}
            className="p-1.5 rounded hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <ChevronRight className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestRow({ req, provisioning }: { req: RouteRequest; provisioning?: { channelId: number; persona: string } }) {
  const unread = !req.resolved;
  return (
    <div className={cn("relative px-3 py-2.5", req.hidden && "opacity-50")}>
      {unread && <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent" aria-hidden />}
      <div className="flex items-center gap-2 mb-1 min-w-0">
        <PlatformBadge platform={req.platform} compact />
        {req.channel && (
          <span className="text-[10px] font-mono text-fg-subtle bg-surface-2 rounded px-1 py-0.5 whitespace-nowrap flex-shrink-0">
            {req.channel}
          </span>
        )}
        {provisioning && !req.resolved && (
          <span title={`This channel auto-routes to ${provisioning.persona} (observer)`}>
            <Pill tone="accent">⚡ auto</Pill>
          </span>
        )}
        <span className={cn("text-sm font-medium truncate", unread ? "text-fg" : "text-fg-muted")}>
          {req.chat_name ?? req.push_name ?? req.sender_jid ?? "Unknown"}
        </span>
        {req.message_count && req.message_count > 1 ? (
          <Pill tone="info">{req.message_count} msgs</Pill>
        ) : null}
        <span
          className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap"
          title={localDateTime(req.last_message_at ?? req.timestamp)}
        >
          {relativeTime(req.last_message_at ?? req.timestamp).replace(" ago", "")}
        </span>
      </div>
      <div className="text-xs text-fg-muted truncate font-mono">
        {req.chat_name && req.push_name && (req.chat_jid.endsWith("@g.us") || req.chat_jid.startsWith("asana:")) ? `${req.push_name} · ` : ""}{req.chat_jid}
      </div>
      {req.text && <div className="text-xs text-fg-subtle truncate mt-0.5">{req.text}</div>}
    </div>
  );
}

// ─── Detail ─────────────────────────────────────────────────────────────

function RequestDetail({
  req,
  personaNames,
  mcpByPersona,
  allRoutes,
  channels,
  provisioning,
  onCreated,
  onHide,
  onDismiss,
  onResolve,
  onWiki,
}: {
  req: RouteRequest;
  personaNames: string[];
  mcpByPersona: Record<string, string[]>;
  allRoutes: Route[];
  channels: Array<{ id: number; name: string; platform: string }>;
  provisioning?: { channelId: number; persona: string };
  onCreated: (routeName: string) => void;
  onHide: () => void;
  onDismiss: () => void;
  onResolve: () => void;
  onWiki: () => void;
}) {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [autoRouting, setAutoRouting] = useState(false);
  async function autoRoute() {
    if (!provisioning) return;
    setAutoRouting(true);
    try {
      const res = await BridgeApi.provisionObservers(provisioning.channelId, { chat: req.chat_jid });
      if (res.error) { toast.error("Auto-route failed", res.error); return; }
      onCreated(`${provisioning.persona} observer`);
    } catch (e) {
      toast.error("Auto-route failed", String(e));
    } finally {
      setAutoRouting(false);
    }
  }
  const { data: conv = [], isLoading } = useQuery({
    queryKey: qk.requests.conversation(req.chat_jid),
    queryFn: () => RequestsApi.conversation(req.chat_jid),
  });

  // Reset creator state when switching requests
  useEffect(() => setCreating(false), [req.chat_jid]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border">
        <div className="flex items-center gap-2 flex-wrap">
          <PlatformBadge platform={req.platform} />
          {req.channel && <Pill tone="info">{req.channel}</Pill>}
          {req.resolved ? (
            <Pill tone="success">resolved</Pill>
          ) : (
            <Pill tone="accent">pending</Pill>
          )}
          {req.chat_kind && <Pill>{req.chat_kind}</Pill>}
          {req.participants && req.participants.length > 0 && (
            <Pill>{req.participants.length} participants</Pill>
          )}
        </div>
        <h2 className="text-base font-semibold text-fg mt-1.5">{req.chat_name ?? req.push_name ?? "Unknown"}</h2>
        <p className="text-xs text-fg-muted font-mono mt-0.5">
          {req.chat_name && req.push_name && (req.chat_jid.endsWith("@g.us") || req.chat_jid.startsWith("asana:")) ? `${req.push_name} · ` : ""}{req.chat_jid}
        </p>
      </div>

      {/* Conversation */}
      <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5 min-h-0 min-w-0">
        {isLoading && <CenteredSpinner />}
        {!isLoading && conv.length === 0 && (
          <div className="text-sm text-fg-muted py-6 text-center">No messages captured.</div>
        )}
        {!isLoading && conv.map((m, i) => <MessageBubble key={i} msg={m} />)}
      </div>

      {/* Footer / actions */}
      <div className="border-t border-border px-5 py-3 space-y-3">
        {!req.resolved && !creating && (
          <div className="space-y-2">
            {provisioning && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" onClick={autoRoute} loading={autoRouting}>
                  <Plus className="size-3.5" />
                  Auto-route → {provisioning.persona} observer
                </Button>
                <span className="text-[11px] text-fg-subtle">
                  Applies this channel's auto-route rule (silent <span className="font-mono">{provisioning.persona}</span> observer) to this chat, and resolves it.
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant={provisioning ? "secondary" : "primary"} onClick={() => setCreating(true)}>
                <Plus className="size-3.5" />
                {provisioning ? "Custom route…" : "Allow — Create Route"}
              </Button>
              <Button size="sm" variant="secondary" onClick={onResolve}>
                <Check className="size-3.5" />
                Resolve
              </Button>
              <Button size="sm" variant="secondary" onClick={onWiki} title="Send this conversation (and any links in it) to the fleet wiki for the curator to extract + tag, then resolve">
                <BookOpen className="size-3.5" />
                Wiki &amp; resolve
              </Button>
              <Button size="sm" variant="ghost" onClick={onHide}>
                <EyeOff className="size-3.5" />
                Hide
              </Button>
              <Button size="sm" variant="ghost" onClick={onDismiss}>
                <XIcon className="size-3.5" />
                Deny &amp; ignore
              </Button>
            </div>
          </div>
        )}
        {creating && (
          <CreateRouteInline
            req={req}
            personaNames={personaNames}
            mcpByPersona={mcpByPersona}
            allRoutes={allRoutes}
            channels={channels}
            onCancel={() => setCreating(false)}
            onCreated={onCreated}
          />
        )}
      </div>
    </div>
  );
}

function MessageBubble({ msg }: { msg: UnmappedMessage }) {
  return (
    <div className={cn("flex", msg.from_me ? "justify-end" : "justify-start")}>
      <div className={cn(
        "max-w-[80%] rounded-md px-3 py-1.5 text-sm",
        msg.from_me ? "bg-accent-subtle text-fg" : "bg-surface-2 text-fg",
      )}>
        {!msg.from_me && (
          <p className="text-[10px] font-medium text-accent mb-0.5">{msg.push_name || msg.sender_jid || "Unknown"}</p>
        )}
        <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{msg.text ?? "—"}</p>
        <p className="text-[10px] text-fg-subtle mt-0.5 text-right tabular-nums" title={msg.ts}>{localDateTime(msg.ts)}</p>
      </div>
    </div>
  );
}

function CreateRouteInline({
  req,
  personaNames,
  mcpByPersona,
  allRoutes,
  channels,
  onCancel,
  onCreated,
}: {
  req: RouteRequest;
  personaNames: string[];
  mcpByPersona: Record<string, string[]>;
  allRoutes: Route[];
  channels: Array<{ id: number; name: string; platform: string }>;
  onCancel: () => void;
  onCreated: (name: string) => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  // Seed the full route editor from this request: default to the channel the
  // message arrived on (so it's scoped to the right account), with the chat
  // pre-matched. Everything else (engagement, proactivity, per-route MCP, assist)
  // is the SAME RouteFields used on the Routes page — full parity, no drift.
  const seedCh = channels.find((c) => c.name === (req.channel ?? "")) ?? channels.find((c) => c.platform === req.platform);
  const [draft, setDraft] = useState<Route>(() => ({
    name: "",
    channel_id: seedCh?.id,
    channel_name: seedCh?.name,
    channel_platform: (seedCh?.platform ?? req.platform) as Platform,
    match: {
      platform: req.platform as Platform,
      channel: seedCh?.name,
      chat_jid: req.chat_jid,
      ...(req.chat_kind ? { chat_kind: req.chat_kind as ChatKind } : {}),
    },
    acl: { inbound: "allow" },
    persona: personaNames[0] ?? "",
    sink_type: "persona",
    behavior: { debounce_ms: 8000, max_defer_ms: 60000, history_limit: 10, wake_mode: "all" },
  }));

  const isPersona = (draft.sink_type ?? "persona") === "persona";
  const canSave = !!draft.name && !!(draft.channel_id ?? draft.channel_name) && (!isPersona || !!draft.persona);

  async function submit() {
    if (!canSave) return;
    setSaving(true);
    try {
      await RequestsApi.createRoute(req.id, draft as Route & { channel?: string });
      onCreated(draft.name);
    } catch (err) {
      toast.error("Create failed", String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel-elevated p-3 space-y-3 animate-fade-in">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-fg uppercase tracking-wider">Create route from this request</h3>
        <button onClick={onCancel} className="text-fg-subtle hover:text-fg"><XIcon className="size-3.5" /></button>
      </div>
      <p className="text-[11px] text-fg-subtle">
        Pre-filled from this chat. Same full options as the Routes page — engagement, proactivity, per-route MCP, assist-observer.
      </p>
      <RouteFields draft={draft} setDraft={setDraft} personaNames={personaNames} mcpByPersona={mcpByPersona} allRoutes={allRoutes} />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={!canSave} loading={saving}>Create route</Button>
      </div>
    </div>
  );
}
